import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Check, Leaf, Star, Sprout, X, Info, Mail, ArrowRight, Copy, Gift, Box, Droplets, Users, FileText, Video, Bell, Package } from 'lucide-react';
import Logo from '../components/Logo';
import PackageCard from '../components/shop/PackageCard';
import Testimonial from '../components/Testimonial';
import MediaCarousel, { MediaItem } from '../components/MediaCarousel';
import { supabase, retryOperation } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useMetaPixel } from '../hooks/useMetaPixel';

type FallBoxType = 'fall_greens' | 'fall_essentials';

const PRODUCT_FEATURES = {
  'Digital Garden Plan': [
    { text: 'Your personalized plan, sent to your email!', icon: Mail },
    { text: 'Digitized, zone‑matched planting plan', icon: FileText, value: 25 },
    { text: 'Onboarding Video & Planting Tutorials', icon: Video },
    { text: 'Tailored care reminders', icon: Bell }
  ],
  'The Easy Grow Box': [
    { text: '10 gallon grow bag', icon: Box, value: 12 },
    { text: '4‑pack of Seeds; best for your growing zone', icon: Sprout, value: 8 },
    { text: 'Organic Fertilizer', icon: Droplets, value: 10 },
    { text: 'Self-watering spike', icon: Droplets, value: 5 }
  ],
  'The Guided Garden Box': [
    { text: '10 gallon grow bag', icon: Box, value: 12 },
    { text: 'Organic Soil (shipped separately)', icon: Package, value: 15 },
    { text: '4‑pack of Seeds; best for your growing zone', icon: Sprout, value: 8 },
    { text: 'Organic Fertilizer', icon: Droplets, value: 10 },
    { text: 'Self-watering spike', icon: Droplets, value: 5 },
    { text: 'A human garden coach', icon: Users, value: 75 }
  ],
  'Common Box Features': [
    { text: 'Digitized, zone‑matched planting plan', icon: FileText, value: 25 },
    { text: 'Onboarding Videos & Tutorials', icon: Video },
    { text: 'Tailored care reminders', icon: Bell }
  ],
} as const;

interface ProductDetails {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  slug: string;
  external_url: string | null;
  description: string;
}

export default function GenericGrowBoxLanding() {
  const { user } = useAuth();
  useMetaPixel(true);
  const [selectedBoxType, setSelectedBoxType] = useState<FallBoxType | null>('fall_greens');
  const [allProducts, setAllProducts] = useState<ProductDetails[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const step2Ref = useRef<HTMLDivElement>(null);
  const emailSectionRef = useRef<HTMLDivElement>(null);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [productToRedirect, setProductToRedirect] = useState<ProductDetails | null>(null);
  const [emailSubmitLoading, setEmailSubmitLoading] = useState(false);
  const [emailSubmitError, setEmailSubmitError] = useState<string | null>(null);
  const [emailSubmitSuccess, setEmailSubmitSuccess] = useState(false);

  const mediaItems: MediaItem[] = [
    {
      type: 'image',
      url: '/Cool Veggie Garden Mock Up.png',
      alt: 'Blooma Grow Box with gardening supplies and fresh vegetables',
      caption: 'Everything you need to start growing'
    },
    {
      type: 'image',
      url: '/Blooma Box Mockup.jpg',
      alt: 'Blooma Grow Box product packaging',
      caption: 'Premium quality grow box'
    },
    {
      type: 'image',
      url: '/Blooma Box Mockup copy.jpg',
      alt: 'Blooma Grow Box contents overview',
      caption: 'Complete growing kit included'
    }
  ];

  const [isGiftPurchase, setIsGiftPurchase] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientZipCode, setRecipientZipCode] = useState('');
  const [shippingToRecipient, setShippingToRecipient] = useState<boolean | null>(null);
  const [notifyRecipient, setNotifyRecipient] = useState(false);
  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const PRODUCT_INFO = {
    'Digital Garden Plan': { slug: 'digital-garden-plan', fallbackPrice: 19, originalPrice: 25 },
    'The Easy Grow Box': { slug: 'starter-supply-kit', fallbackPrice: 49, originalPrice: 59 },
    'The Guided Garden Box': { slug: 'complete-garden-kit', fallbackPrice: 99, originalPrice: 119 }
  } as const;

  const [error, setError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText('FALLFRIENDS');
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  useEffect(() => {
    if (showEmailCapture && emailSectionRef.current) {
      setTimeout(() => {
        emailSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [showEmailCapture]);

  useEffect(() => {
    async function fetchProductDetails() {
      setLoadingProducts(true);
      setError(null);
      try {
        const { data, error: fetchError } = await supabase
          .from('products')
          .select('id, name, price, slug, external_url, description')
          .in('slug', Object.values(PRODUCT_INFO).map(info => info.slug));

        if (fetchError) throw fetchError;

        console.log('Raw product data from Supabase:', data);

        const products: ProductDetails[] = [];
        Object.entries(PRODUCT_INFO).forEach(([productName, info]) => {
          const product = data?.find(p => p.slug === info.slug);
          if (product) {
            console.log(`Found product ${productName}:`, {
              slug: product.slug,
              external_url: product.external_url,
              name: product.name,
              databasePrice: product.price,
              overridePrice: info.fallbackPrice
            });
            products.push({
              id: product.id,
              name: product.name,
              price: info.fallbackPrice,
              originalPrice: info.originalPrice,
              slug: product.slug,
              external_url: product.external_url,
              description: product.description
            });
          } else {
            console.log(`Product not found for ${productName} with slug ${info.slug}`);
            products.push({
              id: `fallback-${info.slug}`,
              name: productName,
              price: info.fallbackPrice,
              originalPrice: info.originalPrice,
              slug: info.slug,
              external_url: null,
              description: `${productName} - Coming Soon`
            });
          }
        });
        console.log('Final products array:', products);
        setAllProducts(products);
      } catch (err) {
        console.error('Error fetching product details:', err);
        setError(err instanceof Error ? err.message : 'Failed to load product details.');
        const fallbackProducts: ProductDetails[] = [];
        Object.entries(PRODUCT_INFO).forEach(([productName, info]) => {
          fallbackProducts.push({
            id: `fallback-${info.slug}`,
            name: productName,
            price: info.fallbackPrice,
            originalPrice: info.originalPrice,
            slug: info.slug,
            external_url: null,
            description: `${productName} - Coming Soon`
          });
        });
        setAllProducts(fallbackProducts);
      } finally {
        setLoadingProducts(false);
      }
    }

    fetchProductDetails();
  }, []);

  const handleCtaClick = (product: ProductDetails, boxType: FallBoxType | null) => {
    setProductToRedirect(product);
    setEmailInput(user?.email || '');
    setShowEmailCapture(true);
    setEmailSubmitError(null);
    setEmailSubmitSuccess(false);
  };

  const handleEarlyCtaClick = () => {
    const easyGrowBox = allProducts.find(p => p.name === 'The Easy Grow Box');
    if (easyGrowBox) {
      handleCtaClick(easyGrowBox, selectedBoxType);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput.trim() || !productToRedirect) return;

    setEmailSubmitLoading(true);
    setEmailSubmitError(null);

    try {
      if (isGiftPurchase) {
        if (!recipientEmail.trim() || !/\S+@\S+\.\S+/.test(recipientEmail)) {
          setEmailSubmitError('Please enter a valid recipient email address.');
          setEmailSubmitLoading(false);
          return;
        }
        if (!recipientZipCode.trim() || recipientZipCode.length !== 5) {
          setEmailSubmitError('Please enter a valid 5-digit zip code for the recipient.');
          setEmailSubmitLoading(false);
          return;
        }
        if (shippingToRecipient === null) {
          setEmailSubmitError('Please select where to ship this gift.');
          setEmailSubmitLoading(false);
          return;
        }
        if (shippingToRecipient) {
          if (!streetAddress.trim()) {
            setEmailSubmitError('Please enter a street address.');
            setEmailSubmitLoading(false);
            return;
          }
          if (!city.trim()) {
            setEmailSubmitError('Please enter a city.');
            setEmailSubmitLoading(false);
            return;
          }
          if (!state) {
            setEmailSubmitError('Please select a state.');
            setEmailSubmitLoading(false);
            return;
          }
        }
      }

      let boxThemeToStore: string;
      if (selectedBoxType === 'fall_greens') {
        boxThemeToStore = 'fall_greens';
      } else if (selectedBoxType === 'fall_essentials') {
        boxThemeToStore = 'cool_weather_veggie';
      } else {
        boxThemeToStore = 'unknown';
      }

      const submissionData: any = {
        email: emailInput.toLowerCase(),
        box_theme: boxThemeToStore,
        source_page: 'grow-box',
        is_gift_purchase: isGiftPurchase
      };

      if (isGiftPurchase) {
        submissionData.recipient_email = recipientEmail.toLowerCase();
        submissionData.recipient_zip_code = recipientZipCode;
        submissionData.shipping_to_recipient = shippingToRecipient;
        submissionData.notify_recipient_via_email = notifyRecipient;

        if (shippingToRecipient) {
          submissionData.street_address = streetAddress;
          submissionData.city = city;
          submissionData.state = state;
        }
      }

      const { error: submissionError } = await supabase
        .from('grow_box_submissions')
        .insert(submissionData);

      if (submissionError) throw submissionError;

      // Subscribe to Mailchimp newsletter
      if (!user) {
        const response = await fetch('/.netlify/functions/subscribe-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            email: emailInput.toLowerCase(),
            tags: ['Website', 'Grow Box Purchase'],
            source: 'Grow Box Checkout'
          })
        });

        if (!response.ok) {
          console.error('Failed to subscribe to newsletter');
          // Don't throw - we still want them to proceed to checkout
        }
      }

      setEmailSubmitSuccess(true);

      setTimeout(() => {
        if (productToRedirect?.external_url) {
          const finalUrl = selectedBoxType
            ? `${productToRedirect.external_url}?box_type=${selectedBoxType}`
            : productToRedirect.external_url;
          window.location.href = finalUrl;
        }
      }, 1500);

    } catch (err) {
      console.error('Error submitting email:', err);
      setEmailSubmitError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setEmailSubmitLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F2EE] flex flex-col items-center justify-center p-4">
      <div className="max-w-5xl w-full bg-white rounded-lg shadow-xl p-8 relative z-10">
        <div className="flex justify-center mb-6">
          <Logo size="lg" showText={false} variant="full" />
        </div>

        <h1 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-900">
          Claim Your Free Fall Grow Box — Ready to Harvest by Thanksgiving!
        </h1>
        <p className="text-lg text-center text-gray-600 mb-6">
          Harvest baby greens in 3–4 weeks. Just plant and enjoy — no experience needed. In 5 minutes a week.
        </p>

        <div className="max-w-2xl mx-auto mb-8">
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-dashed border-[#EA9163] rounded-xl p-6 shadow-md">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Gift className="h-6 w-6 text-[#EA9163]" />
              <h3 className="text-lg font-bold text-gray-900">End of Season Special</h3>
            </div>
            <p className="text-center text-gray-700 mb-4 text-sm">
              Use this code at checkout to claim your free grow box
            </p>
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="bg-white px-6 py-3 rounded-lg border-2 border-[#EA9163] shadow-sm">
                <code className="text-2xl md:text-3xl font-bold text-[#EA9163] tracking-wider select-all">
                  FALLFRIENDS
                </code>
              </div>
              <button
                onClick={handleCopyCode}
                className="flex items-center gap-2 px-4 py-3 bg-[#EA9163] text-white rounded-lg hover:bg-[#d67d4f] transition-colors font-medium shadow-sm"
                aria-label="Copy coupon code"
              >
                {copiedCode ? (
                  <>
                    <Check className="h-5 w-5" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-5 w-5" />
                    <span>Apply at Checkout</span>
                  </>
                )}
              </button>
            </div>
            <p className="text-center text-xs text-gray-600">
              Click "Claim My Free Grow Box" below, then use code FALLFRIENDS at checkout
            </p>
          </div>
        </div>

        <div className="max-w-md mx-auto mt-8">
          <button
            onClick={handleEarlyCtaClick}
            disabled={loadingProducts || showEmailCapture}
            className="w-full bg-primary-500 text-white py-4 px-6 rounded-lg hover:bg-primary-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-bold text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5"
          >
            {loadingProducts ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <>
                <Leaf className="h-6 w-6" />
                <span>Claim My Box</span>
                <ArrowRight className="h-6 w-6" />
              </>
            )}
          </button>
          <p className="text-center text-sm text-gray-600 mt-3">
            Free with code FALLFRIENDS • Limited time offer
          </p>
        </div>

        <Testimonial
          quote="Such a fun and easy way to grow greens! My kids loved it."
          customerName="Amanda"
          zone="Zone 6b"
        />

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
            <X className="h-5 w-5" />
            <span>{error}</span>
          </div>
        )}

        {/* Media Carousel */}
        <div className="mt-8 flex justify-center">
          <div className="w-full max-w-sm md:max-w-md lg:max-w-lg mx-auto">
            <MediaCarousel
              items={mediaItems}
              autoPlayInterval={5000}
              showControls={true}
              className="shadow-md"
            />
          </div>
        </div>

        <Testimonial
          quote="It came with everything I needed, a grow bag, organic fertilizer, and a self watering spike. The instructions are clear and easy to use. There's even quick mini videos. My little garden is actually flourishing! 🥬💛."
          customerName="Karla"
          zone="Zone 5a"
        />

        <div className="max-w-md mx-auto mt-8">
          <button
            onClick={handleEarlyCtaClick}
            disabled={loadingProducts || showEmailCapture}
            className="w-full bg-primary-500 text-white py-4 px-6 rounded-lg hover:bg-primary-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-bold text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5"
          >
            {loadingProducts ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <>
                <Leaf className="h-6 w-6" />
                <span>Claim My Box</span>
                <ArrowRight className="h-6 w-6" />
              </>
            )}
          </button>
          <p className="text-center text-sm text-gray-600 mt-3">
            Everything you need to start your fall garden
          </p>
        </div>

        <div
          ref={step2Ref}
          className={`mt-12 transition-all duration-300 ${showEmailCapture ? 'pointer-events-none' : ''}`}
        >
          {showEmailCapture && (
            <p className="text-center text-gray-500 mb-6">
              Complete Step 2 below to proceed with your selection
            </p>
          )}
          {loadingProducts ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
            </div>
          ) : (
            <div className="flex justify-center">
              {allProducts.filter(product => product.name === 'The Easy Grow Box').map(product => {
                const productFeatures = PRODUCT_FEATURES[product.name as keyof typeof PRODUCT_FEATURES] || [];
                const commonFeatures = PRODUCT_FEATURES['Common Box Features'];
                const allFeatures = [...productFeatures, ...commonFeatures];

                const totalRetailValue = allFeatures.reduce((sum, feature) => {
                  return sum + (typeof feature === 'object' && feature.value ? feature.value : 0);
                }, 0);

                return (
                  <div key={product.id} className="max-w-md">
                    <PackageCard
                      product={product}
                      highlighted={true}
                      ctaLabel="Claim My Box"
                      features={allFeatures}
                      disabled={showEmailCapture}
                      selectedBoxType={selectedBoxType}
                      onCtaClick={handleCtaClick}
                      showValueSummary={true}
                      totalRetailValue={totalRetailValue}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {showEmailCapture && (
          <div
            ref={emailSectionRef}
            className="mt-12 transition-all duration-300"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="bg-primary-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                2
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Last step! Who's growing?
              </h2>
            </div>

            <div className="max-w-md mx-auto bg-white border-2 border-primary-200 rounded-lg p-6">
              {emailSubmitSuccess ? (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-green-600 mb-4">
                    <Check className="h-6 w-6" />
                    <p className="font-medium">Success!</p>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Hang tight! We're redirecting you to the checkout page
                  </p>
                  <p className="text-sm text-gray-500">
                    Redirecting you to get started...
                  </p>
                </div>
              ) : (
                <>
                  <form onSubmit={handleEmailSubmit} className="space-y-6">
                    <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <input
                        type="checkbox"
                        id="isGift"
                        checked={isGiftPurchase}
                        onChange={(e) => setIsGiftPurchase(e.target.checked)}
                        className="h-5 w-5 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <label htmlFor="isGift" className="flex items-center gap-2 text-gray-800 font-medium cursor-pointer">
                        <Gift className="h-5 w-5 text-primary-500" />
                        <span>This is a gift</span>
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {isGiftPurchase ? 'Your Email (Purchaser)' : 'Your Email'}
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          value={emailInput}
                          onChange={(e) => setEmailInput(e.target.value)}
                          placeholder="Enter your email address"
                          className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                          required
                        />
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        We'll send order updates here
                      </p>
                    </div>

                    {isGiftPurchase && (
                      <div className="space-y-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Gift className="h-5 w-5 text-green-600" />
                          Gift Recipient Information
                        </h3>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Recipient's Email Address
                          </label>
                          <div className="relative">
                            <input
                              type="email"
                              value={recipientEmail}
                              onChange={(e) => setRecipientEmail(e.target.value)}
                              placeholder="recipient@example.com"
                              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                              required
                            />
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Recipient's Zip Code
                          </label>
                          <input
                            type="text"
                            maxLength={5}
                            pattern="[0-9]*"
                            value={recipientZipCode}
                            onChange={(e) => setRecipientZipCode(e.target.value.replace(/\D/g, ''))}
                            placeholder="12345"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                            required
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Needed for zone-specific gardening plan
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Where should we ship this gift?
                          </label>
                          <div className="space-y-2">
                            <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors hover:bg-white border-gray-300 hover:border-primary-300">
                              <input
                                type="radio"
                                name="shippingDestination"
                                checked={shippingToRecipient === false}
                                onChange={() => setShippingToRecipient(false)}
                                className="mt-0.5 h-4 w-4 text-primary-500 border-gray-300 focus:ring-primary-500"
                                required
                              />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">Ship to me</div>
                                <div className="text-sm text-gray-600">I'll give it to them in person</div>
                              </div>
                            </label>
                            <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors hover:bg-white border-gray-300 hover:border-primary-300">
                              <input
                                type="radio"
                                name="shippingDestination"
                                checked={shippingToRecipient === true}
                                onChange={() => setShippingToRecipient(true)}
                                className="mt-0.5 h-4 w-4 text-primary-500 border-gray-300 focus:ring-primary-500"
                                required
                              />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">Ship directly to recipient</div>
                                <div className="text-sm text-gray-600">Send it straight to them</div>
                              </div>
                            </label>
                          </div>
                        </div>

                        {shippingToRecipient && (
                          <div className="space-y-4 pt-4 border-t border-green-300">
                            <h4 className="font-medium text-gray-900">Recipient's Shipping Address</h4>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Street Address
                              </label>
                              <input
                                type="text"
                                value={streetAddress}
                                onChange={(e) => setStreetAddress(e.target.value)}
                                placeholder="123 Main St"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                required
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  City
                                </label>
                                <input
                                  type="text"
                                  value={city}
                                  onChange={(e) => setCity(e.target.value)}
                                  placeholder="City"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                  required
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  State
                                </label>
                                <select
                                  value={state}
                                  onChange={(e) => setState(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                  required
                                >
                                  <option value="">Select</option>
                                  <option value="AL">AL</option>
                                  <option value="AK">AK</option>
                                  <option value="AZ">AZ</option>
                                  <option value="AR">AR</option>
                                  <option value="CA">CA</option>
                                  <option value="CO">CO</option>
                                  <option value="CT">CT</option>
                                  <option value="DE">DE</option>
                                  <option value="FL">FL</option>
                                  <option value="GA">GA</option>
                                  <option value="HI">HI</option>
                                  <option value="ID">ID</option>
                                  <option value="IL">IL</option>
                                  <option value="IN">IN</option>
                                  <option value="IA">IA</option>
                                  <option value="KS">KS</option>
                                  <option value="KY">KY</option>
                                  <option value="LA">LA</option>
                                  <option value="ME">ME</option>
                                  <option value="MD">MD</option>
                                  <option value="MA">MA</option>
                                  <option value="MI">MI</option>
                                  <option value="MN">MN</option>
                                  <option value="MS">MS</option>
                                  <option value="MO">MO</option>
                                  <option value="MT">MT</option>
                                  <option value="NE">NE</option>
                                  <option value="NV">NV</option>
                                  <option value="NH">NH</option>
                                  <option value="NJ">NJ</option>
                                  <option value="NM">NM</option>
                                  <option value="NY">NY</option>
                                  <option value="NC">NC</option>
                                  <option value="ND">ND</option>
                                  <option value="OH">OH</option>
                                  <option value="OK">OK</option>
                                  <option value="OR">OR</option>
                                  <option value="PA">PA</option>
                                  <option value="RI">RI</option>
                                  <option value="SC">SC</option>
                                  <option value="SD">SD</option>
                                  <option value="TN">TN</option>
                                  <option value="TX">TX</option>
                                  <option value="UT">UT</option>
                                  <option value="VT">VT</option>
                                  <option value="VA">VA</option>
                                  <option value="WA">WA</option>
                                  <option value="WV">WV</option>
                                  <option value="WI">WI</option>
                                  <option value="WY">WY</option>
                                </select>
                              </div>
                            </div>

                            <div className="flex items-start gap-2 mt-4">
                              <input
                                type="checkbox"
                                id="notifyRecipient"
                                checked={notifyRecipient}
                                onChange={(e) => setNotifyRecipient(e.target.checked)}
                                className="mt-1 h-4 w-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                              />
                              <label htmlFor="notifyRecipient" className="text-sm text-gray-700 cursor-pointer">
                                Notify recipient via email about this gift
                                <span className="block text-xs text-gray-500 mt-1">
                                  We'll send them an email letting them know a gift is on the way
                                </span>
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {emailSubmitError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                        <X className="h-5 w-5" />
                        <span className="text-sm">{emailSubmitError}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={emailSubmitLoading || !emailInput.trim()}
                      className="w-full bg-primary-500 text-white py-3 px-4 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-semibold"
                    >
                      {emailSubmitLoading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          <span>Complete Your Order</span>
                          <ArrowRight className="h-5 w-5" />
                        </>
                      )}
                    </button>

                    <p className="text-xs text-gray-500 text-center">
                      By continuing, you agree to receive updates about your personalized garden plan and growing tips.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
